import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

const AssetCategoriesTab = () => {
  const [categories, setCategories] = useState([
    {
      id: 1,
      name: "Laptops",
      code: "LAP",
      description: "Portable computing devices",
      fields: ["Serial Number", "Model", "RAM", "Storage"],
      isActive: true,
      createdDate: "2024-01-15",
      assetCount: 156
    },
    {
      id: 2,
      name: "Desktop Computers",
      code: "DSK",
      description: "Fixed workstation computers",
      fields: ["Serial Number", "Model", "CPU", "RAM", "Storage"],
      isActive: true,
      createdDate: "2024-01-15",
      assetCount: 89
    },
    {
      id: 3,
      name: "Servers",
      code: "SRV",
      description: "Server hardware and infrastructure",
      fields: ["Serial Number", "Model", "CPU", "RAM", "Storage", "Rack Position"],
      isActive: true,
      createdDate: "2024-01-15",
      assetCount: 24
    },
    {
      id: 4,
      name: "Network Equipment",
      code: "NET",
      description: "Routers, switches, and network devices",
      fields: ["Serial Number", "Model", "Port Count", "Firmware Version"],
      isActive: true,
      createdDate: "2024-02-01",
      assetCount: 45
    },
    {
      id: 5,
      name: "Printers",
      code: "PRT",
      description: "Printing devices and multifunction units",
      fields: ["Serial Number", "Model", "Print Type", "Toner Model"],
      isActive: false,
      createdDate: "2024-01-20",
      assetCount: 32
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    code: "",
    description: "",
    fields: [],
    isActive: true
  });

  const [newField, setNewField] = useState("");

  const fieldTypeOptions = [
    { value: "text", label: "Text Field" },
    { value: "number", label: "Number Field" },
    { value: "date", label: "Date Field" },
    { value: "select", label: "Dropdown Selection" },
    { value: "boolean", label: "Yes/No Checkbox" }
  ];

  const handleAddCategory = () => {
    if (newCategory?.name && newCategory?.code) {
      const category = {
        id: Date.now(),
        ...newCategory,
        createdDate: new Date()?.toISOString()?.split('T')?.[0],
        assetCount: 0
      };
      setCategories(prev => [...prev, category]);
      setNewCategory({
        name: "",
        code: "",
        description: "",
        fields: [],
        isActive: true
      });
      setShowAddModal(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({ ...category });
    setShowAddModal(true);
  };

  const handleUpdateCategory = () => {
    setCategories(prev =>
      prev?.map(cat =>
        cat?.id === editingCategory?.id ? { ...newCategory, id: editingCategory?.id } : cat
      )
    );
    setEditingCategory(null);
    setNewCategory({
      name: "",
      code: "",
      description: "",
      fields: [],
      isActive: true
    });
    setShowAddModal(false);
  };

  const handleDeleteCategory = (id) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      setCategories(prev => prev?.filter(cat => cat?.id !== id));
    }
  };

  const handleToggleStatus = (id) => {
    setCategories(prev =>
      prev?.map(cat =>
        cat?.id === id ? { ...cat, isActive: !cat?.isActive } : cat
      )
    );
  };

  const addField = () => {
    if (newField?.trim()) {
      setNewCategory(prev => ({
        ...prev,
        fields: [...prev?.fields, newField?.trim()]
      }));
      setNewField("");
    }
  };

  const removeField = (index) => {
    setNewCategory(prev => ({
      ...prev,
      fields: prev?.fields?.filter((_, i) => i !== index)
    }));
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingCategory(null);
    setNewCategory({
      name: "",
      code: "",
      description: "",
      fields: [],
      isActive: true
    });
    setNewField("");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Asset Categories</h3>
          <p className="text-sm text-muted-foreground">Manage asset types and their custom fields</p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowAddModal(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Category
        </Button>
      </div>
      {/* Categories List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {categories?.map((category) => (
          <div key={category?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold ${
                  category?.isActive ? 'bg-primary' : 'bg-muted-foreground'
                }`}>
                  {category?.code}
                </div>
                <div>
                  <h4 className="font-semibold text-card-foreground">{category?.name}</h4>
                  <p className="text-sm text-muted-foreground">{category?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  category?.isActive 
                    ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  {category?.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Assets:</span>
                <span className="font-medium text-card-foreground">{category?.assetCount}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Created:</span>
                <span className="font-medium text-card-foreground">{category?.createdDate}</span>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-2">Custom Fields:</p>
                <div className="flex flex-wrap gap-1">
                  {category?.fields?.map((field, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleToggleStatus(category?.id)}
                iconName={category?.isActive ? "Pause" : "Play"}
                iconPosition="left"
              >
                {category?.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditCategory(category)}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteCategory(category?.id)}
                  iconName="Trash2"
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Category Name"
                  type="text"
                  value={newCategory?.name}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., Mobile Devices"
                  required
                />
                
                <Input
                  label="Category Code"
                  type="text"
                  value={newCategory?.code}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, code: e?.target?.value?.toUpperCase() }))}
                  placeholder="e.g., MOB"
                  description="3-4 character unique identifier"
                  required
                />
              </div>

              <Input
                label="Description"
                type="text"
                value={newCategory?.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e?.target?.value }))}
                placeholder="Brief description of this asset category"
              />

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Custom Fields
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="text"
                      value={newField}
                      onChange={(e) => setNewField(e?.target?.value)}
                      placeholder="Enter field name"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={addField}
                      iconName="Plus"
                      disabled={!newField?.trim()}
                    >
                      Add
                    </Button>
                  </div>
                  
                  {newCategory?.fields?.length > 0 && (
                    <div className="space-y-2">
                      {newCategory?.fields?.map((field, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                          <span className="text-sm text-card-foreground">{field}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeField(index)}
                            iconName="X"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Checkbox
                label="Active Category"
                description="Allow new assets to be created in this category"
                checked={newCategory?.isActive}
                onChange={(e) => setNewCategory(prev => ({ ...prev, isActive: e?.target?.checked }))}
              />
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingCategory ? handleUpdateCategory : handleAddCategory}
                disabled={!newCategory?.name || !newCategory?.code}
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetCategoriesTab;